<script lang="ts" setup>
import { DefaultButton } from '@hantecmarket/hantec-ui-widget'
import { useForm, Field } from 'vee-validate'
import { onMounted, ref } from 'vue'
import * as yup from 'yup'

import HeroBanner from '@/components/HeroBanner/HeroBanner.vue'
import SuccessPopup from '@/components/SuccessPopup.vue'
import { getLanguage } from '@/helpers/language-helper'
import updatePreferenceCenter from '@/services/Forms/updatePreferenceCenter'
import { useGlobalSeoMetaHandler } from '@/services/seoGlobalService'
import {
  fetchTemplateOneStore,
  useTemplateOneData,
  useSeoIndexFollowUnsubscribe
} from '@/services/Trading/templateOneService'
import { __ } from '@/utils/utils'

interface Fields {
  title: string
  description: string
  sub_title: string
  sub_description: string
  footer_text: string
  privacy_policy_label: string
  privacy_policy_pdf: string
  email_label: string
  email_hint: string
  email_subscribe_button: string
  email_unsubscribe_button: string
  email_error_invalid: string
  email_error_required: string
}

const fields = ref<Fields>()

onMounted(async () => {
  await fetchTemplateOneStore('unsubscribe', '')
  fields.value = useTemplateOneData().value?.fields
  useGlobalSeoMetaHandler(useSeoIndexFollowUnsubscribe())
})

const lang = ref<any>('')

onMounted(() => {
  lang.value = getLanguage()
})

const isSubmitting = ref(false)
const isFormSubmitted = ref(false)
const showSuccessPopup = ref(false)
const buttonState = ref('')

const trySubmit = async (value: string) => {
  isFormSubmitted.value = true
  buttonState.value = value
}

const schema = yup.object({
  email: yup
    .string()
    .email(fields.value?.email_error_invalid)
    .required(fields.value?.email_error_required)
})

const { handleSubmit, setErrors } = useForm<FormValues>({
  validationSchema: schema
})

interface FormValues {
  email: string
}

const onSubmit = handleSubmit(async (values: FormValues) => {
  if (!values.email) {
    return
  }

  isSubmitting.value = true

  try {
    const envVar = import.meta.env.VITE_MOE_ENV

    const getRequestBody: any = {
      action: 'get',
      email: values.email
    }

    const requestBody: any = {
      action: 'post',
      email: values.email,
      parameters: {
        moe_sms_subscription: buttonState.value === 'unsubscribe' ? 'OPTOUT' : 'OPTIN',
        moe_wa_subscription: buttonState.value === 'unsubscribe' ? 'OPTOUT' : 'OPTIN',
        marketing_email_subscribed: buttonState.value !== 'unsubscribe'
      },
      update_categories: {
        Marketing_Consent: buttonState.value !== 'unsubscribe'
      }
    }

    if (envVar) {
      requestBody.env = envVar
      getRequestBody.env = envVar
    }

    const responseGet = await updatePreferenceCenter.post(getRequestBody)

    if (responseGet.status !== 200 || !responseGet.data.user_details?.email) {
      setErrors({
        email: fields.value?.email_error_invalid ?? __('Invalid email')
      })
      return
    }

    const response = await updatePreferenceCenter.post(requestBody)

    if (response.status !== 200) {
      throw new Error('Failed to unsubscribe')
    }

    showSuccessPopup.value = true
  } catch (error) {
    console.error('Error updating preferences:', error)
  } finally {
    isSubmitting.value = false
  }
})

const closePopup = () => {
  showSuccessPopup.value = false
  buttonState.value = ''
}
</script>

<template>
  <HeroBanner :title="fields?.title" with-breadcrumbs />

  <section class="flex justify-center w-full pt-20 pb-20 lg:pb-40 lg:pt-40">
    <div class="flex flex-col w-full px-2.5 md:px-30 items-center">
      <h2 class="h3-bold md:h2-bold text-center">{{ fields?.sub_title }}</h2>
      <p class="text-midGrey-100 h6-regular md:subtitle text-center pt-2.5">
        {{ fields?.sub_description }}
      </p>

      <div class="mt-10 md:mt-15 md:mb-20">
        <form @submit.prevent="onSubmit" novalidate>
          <label for="email" class="block mb-1 md:mb-2.5">{{ fields?.email_label }}</label>
          <Field id="email" name="email" class="w-full p-4" v-slot="{ field, errors }">
            <input
              v-bind="field"
              type="email"
              class="w-full bg-gray-50 border-none h-12.5"
              :placeholder="fields?.email_hint"
              v-validate="'required'"
            />
            <span class="text-hantec-primary">{{ errors[0] }}</span>
          </Field>

          <div class="flex flex-col md:flex-row gap-2 w-full">
            <DefaultButton
              :disabled="isSubmitting"
              class="w-full md:w-1/2 lg:px-[60px] button bg-hantec-primary text-white text-center px-8 py-4 rounded-1.5 sm:flex items-center md:inline-flex h-12.5 mt-2 md:mt-5 md:mb-0 justify-center"
              :class="{ 'pointer-events-none bg-gray-600 disabled': isSubmitting }"
              type="submit"
              @click="trySubmit('unsubscribe')"
            >
              <div class="">{{ fields?.email_unsubscribe_button }}</div>
            </DefaultButton>

            <DefaultButton
              :disabled="isSubmitting"
              class="w-full md:w-1/2 lg:px-[60px] py-4 px-8 flex items-center button rounded-1.5 leading-normal border-[1px] font-bold h-12.5 justify-center bg-transparent md:mt-5 mb-1 md:mb-0"
              :class="{ 'pointer-events-none bg-gray-600 disabled': isSubmitting }"
              type="submit"
              @click="trySubmit('subscribe')"
            >
              <div class="">{{ fields?.email_subscribe_button }}</div>
            </DefaultButton>
          </div>
        </form>
      </div>

      <div class="mt-5 butter-wysiwyg">
        {{ fields?.footer_text }}
        <a href="/privacy-policy.pdf" class="mt-5 butter-wysiwyg">
          {{ fields?.privacy_policy_label }}
        </a>
      </div>
    </div>
  </section>

  <SuccessPopup
    :is-open="showSuccessPopup"
    :message="
      buttonState === 'unsubscribe'
        ? __('You are now unsubscribed.')
        : __('You are now subscribed.')
    "
    @close="closePopup"
  />
</template>

<style scoped></style>
